import { FC, Fragment } from 'react'
import {
  SectionArticlesTitle,
} from './styles'
import { RenewalData } from 'src/dtos/renewalData'
import { handleYesNoAnswers, lookupYearsInPrivatePractice } from 'src/utils/formDataManipulation'
import { IoMdCheckmark } from 'react-icons/io'
import { onlyICOCAttorneys, allActiveAttorneys, attorneyFullName } from 'src/utils/renewals'
import { TbFileCheck } from 'react-icons/tb'

interface AttorneysSectionProps {
  renewalData?: RenewalData;
  isComplete: boolean;
}

const RenewalAttorneysSection: FC<AttorneysSectionProps> = ({ renewalData, isComplete }) => {
  const icocAttorneys = renewalData ? onlyICOCAttorneys(renewalData) : []
  const activeAttorneys = renewalData ? allActiveAttorneys(renewalData) : []

  return (
    <section>
      <header>
        <h4>Attorney</h4>
        {isComplete ? (
          <h6><IoMdCheckmark/> Complete</h6>
        ) : null}
      </header>
      <SectionArticlesTitle>ATTORNEYS</SectionArticlesTitle>
      {(activeAttorneys && activeAttorneys.length > 0) ? 
        activeAttorneys?.map((attorney, index) => (
        <article key={index}>
          <strong>{attorneyFullName(attorney)}</strong>
          <span><h6>Position:</h6> {attorney?.position}</span>
          <span><h6>Email:</h6> {attorney?.emailAddress}</span>
          {(renewalData?.renewalSection?.newAttorneys?.some(x => x.barCardNumber === attorney.barCardNumber )) ? <span>
            <h6>Years in Private Practice:</h6>
            {
              lookupYearsInPrivatePractice(attorney?.yearsInPrivatePractice)
            }
          </span> : null}
          <span><h6>License in Texas:</h6> {!!attorney?.license?.isLicensedInTexas ? 'Yes' : 'No'}</span>
          {!!attorney?.license?.isLicensedInTexas && (
            <>
              <span><h6>Texas Bar Card Number:</h6> {attorney?.barCardNumber}</span>
              <span><h6>Date Licensed in Texas:</h6> {attorney?.license?.licenseDate}</span>
            </>
          )}
          <span><h6>License in Another State:</h6> {attorney?.license?.isLicensedInOtherState ? 'Yes' : 'No'}</span>
          {!!attorney?.license?.isLicensedInOtherState && (
            <>
              <span><h6>Other State Bar Card Number:</h6> {attorney?.outSideTexasBarCardNumber}</span>
              <span><h6>Another State of License:</h6> {attorney?.license?.otherState}</span>
              <span><h6>License Date in Another State:</h6> {attorney?.license?.dateLicensedInOtherState}</span>
              <span><h6>Percentage of time "<u>CURRENTLY</u>" practicing in this jurisdiction :</h6> {attorney?.license?.percentageOfWorkSpent}</span>
              <span><h6>License(s) in Another State Info:</h6> {attorney?.license?.licensesInfo}</span>
            </>
          )}
          {!!attorney?.workHistory?.joinedDate && (
            <span><h6>Date joined present firm:</h6> {attorney?.workHistory?.joinedDate}</span>
          )}
          <span><h6>Average hours per week anticipated during the next year for this firm:</h6> {attorney?.workHistory?.hoursPerWeek}</span>
        </article>
      )) : (
        <article>
          <span>N/A</span>
        </article>
      )}
      {renewalData?.renewalSection?.hasLeavingAttorneys === true ? (
        <>
          <SectionArticlesTitle>DEPARTING ATTORNEYS</SectionArticlesTitle>
          {renewalData?.renewalSection?.departingAttorneys?.map((attorney) => (
            <article key={attorney.attorneyName}>
              <strong>{attorney?.attorneyName ?? 'N/A'}</strong>
              <span><h6>Date they left firm:</h6> {
                !!attorney?.leavingDate 
                ? attorney?.leavingDate.includes('T') ? attorney?.leavingDate.split('T')[0] : attorney?.leavingDate
                : 'N/A'
              }</span>
            </article>
          ))}
        </>
      ) : null}
      {activeAttorneys.length === 1 ? (
        <>
          <SectionArticlesTitle>SOLE PRACTITIONER</SectionArticlesTitle>
          <article>
            <strong>
              Average hours worked per week for this firm during the last 12 months, not including work performed as an Independent Contractor or Of Counsel.
            </strong>
            <span>{renewalData?.attorneysSection?.solePractitionerLastYear ?? 'N/A'}</span>
          </article>
          <article>
            <strong>
              Estimated average hours worked per week for this firm during the next 12 months, not including work performed as an Independent Contractor or Of Counsel.
            </strong>
            <span>{renewalData?.attorneysSection?.solePractitionerThisYear ?? 'N/A'}</span>
          </article>
          <article>
            <strong>
              If less than 40 hours, please provide the reason:
            </strong>
            <span>{renewalData?.attorneysSection?.reasonLessThanFourtyHours ?? 'N/A'}</span>
          </article>
        </>
      ) : null}
      {icocAttorneys.length > 0 ? (
        <>
          <SectionArticlesTitle>INDEPENDENT CONTRACTOR/OF COUNSEL</SectionArticlesTitle>
          {icocAttorneys?.map(attorney => (
            <Fragment key={attorney.barCardNumber}>
              <article>
                <strong>{[attorney?.firstName, attorney?.lastName].join(' ')}</strong>
                <span><h6>Year attorney became Independent Contractor or Of Counsel</h6></span>
                <span>{attorney?.yearStartedAsICOC ?? 'N/A'}</span>
                <span><h6>In the past 12 months, how many hours did the Attorney bill on behalf of your firm?</h6></span>
                <span>{attorney?.hoursOnBill ?? 'N/A'}</span>
                <span><h6>
                If the attorney has been acting as Independent Contractor or Of Counsel for your firm for less than 12 months, how many hours do you 
                estimate the attorney will bill on behalf of your firm during their first year?
                </h6></span>
                <span>{attorney?.hoursBilledInFirstYear ?? 'N/A'}</span>
                <span><h6>Does the attorney also practice independently from your firm?</h6></span>
                <span>{handleYesNoAnswers(attorney?.actsIndependentlyFromFirm)}</span>
                {attorney?.actsIndependentlyFromFirm === true ? (
                  <>
                    <span><h6>Where does the attorney practice independently from your firm? </h6></span>
                    <span>{attorney?.actsIndependentlyFromFirmWhere ?? 'N/A'}</span>
                  </>
                ) : null}
                <span><h6>Does the Attorney have professional liability insurance for that independent practice?</h6></span>
                <span>{handleYesNoAnswers(attorney?.hasInsuranceForIndependentPractice)}</span>
                {attorney?.hasInsuranceForIndependentPractice === true ? (
                  <>
                    <span><h6>Name of the insurance company? </h6>{attorney?.insuranceCompanyNameOfIndependent ?? 'N/A'}</span>
                    <span><h6>Effective dates of the policy:</h6></span>
                    <span><h6>From: </h6>{attorney?.effectivePolicyDateFrom ?? 'N/A'}</span>
                    <span><h6>To: </h6>{attorney?.effectivePolicyDateTo ?? 'N/A'}</span>
                    <span><h6>Limits of liability of the policy: </h6>{attorney?.liabilityLimitsOfPolicy ? attorney?.liabilityLimitsOfPolicy : 'N/A'}</span>
                  </>
                ) : null}
                <span><h6>Which of the following tasks does the attorney perform on behalf of your firm?</h6></span>
                {(attorney?.tasksPerformed && attorney?.tasksPerformed.length > 0) ? 
                attorney.tasksPerformed.map(task => (
                  <span key={task}>{task ?? 'N/A'}</span>
                )) : null}
              </article>
            </Fragment>
          ))}
        </>
      ) : null}
      <SectionArticlesTitle>REVIEW</SectionArticlesTitle>
      <article>
        <strong>Notes</strong>
        <span>{renewalData?.attorneysSection?.review?.note ?? 'N/A'}</span>
      </article>
      <article>
        <strong>Files</strong>
        {!!renewalData?.attorneysSection?.review?.files && renewalData?.attorneysSection?.review?.files.length > 0 ? 
          renewalData?.attorneysSection?.review?.files.map(file => (
            <span key={file?.id} className="icon"><TbFileCheck /> {file?.fileName ?? "N/A"}</span>
          )) : (
            <span>N/A</span>
          )}
      </article>
    </section>
  )
}

export default RenewalAttorneysSection